<script setup lang="ts">
import { computed, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";

import debounce from "lodash/debounce";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { mapEnvironmentToWorkPreferencesPatchBody } from "@/modules/worker/helpers/normalizeWorkerEnvironmentFromApi";
import workerService from "@/modules/worker/services/workerService";

import type { WorkerDetail, WorkPreferencesTeamPreference } from "@/modules/worker/types";
import type {
	Environment,
	WorkerPreferences,
} from '@/types/worker';

import FormSection from "@/dsComponents/FormSection.vue";
import Select from "@/dsComponents/inputs/Select.vue";
import Separator from "@/dsComponents/Separator.vue";
import Toggle from "@/dsComponents/Toggle/Toggle.vue";

import WorkerAvailabilities from "@/modules/worker/components/workerAvailabilities/WorkerAvailabilities.vue";

interface WorkerDetailWithEnvironment extends WorkerDetail {
    environment?: Partial<Environment>;
}

interface PreferencesTabProps {
    worker: WorkerDetail;
    workerId: string;
    refreshWorker?: () => Promise<void>;
}

const props = defineProps<PreferencesTabProps>();
const preferences = defineModel<Record<string, unknown>>("preferences", { default: () => ({}) });
const { t } = useI18n();
const { toastError } = useDefaultToast();

const DEBOUNCE_MS = 800;

let debouncedPatchAvailabilities: ReturnType<typeof debounce> | null = null;
let debouncedPersistWorkConditions: ReturnType<typeof debounce> | null = null;

const rebuildDebouncedPersisters = () => {
    debouncedPatchAvailabilities?.cancel();
    debouncedPersistWorkConditions?.cancel();

    debouncedPatchAvailabilities = debounce((payload: WorkerPreferences) => {
        void (async () => {
            if (!payload.regularAvailabilities?.length) {
                return;
            }
            try {
                await workerService.patchRegularAvailabilities(props.workerId, {
                    regularAvailabilities: payload.regularAvailabilities,
                });
                props.worker.regularAvailabilities = payload.regularAvailabilities;
                await props.refreshWorker?.();
            } catch {
                toastError();
            }
        })();
    }, DEBOUNCE_MS);

    debouncedPersistWorkConditions = debounce(() => {
        void (async () => {
            const teamPref =
                (preferences.value.teamPreference as string | undefined) ?? props.worker.teamPreference;
            const env = resolvedEnvironment.value;
            try {
                await workerService.patchWorkPreferences(props.workerId, {
                    teamPreference: teamPref as WorkPreferencesTeamPreference,
                    environment: mapEnvironmentToWorkPreferencesPatchBody(env),
                });
                props.worker.teamPreference = teamPref;
                (props.worker as WorkerDetailWithEnvironment).environment = env;
                await props.refreshWorker?.();
            } catch {
                toastError();
            }
        })();
    }, DEBOUNCE_MS);
};

rebuildDebouncedPersisters();

onBeforeUnmount(() => {
    debouncedPatchAvailabilities?.cancel();
    debouncedPersistWorkConditions?.cancel();
});

const ENVIRONMENT_KEYS: (keyof Environment)[] = [
    "negativeCold",
    "positiveCold",
    "noise",
    "smells",
    "dust",
    "sustainedPace",
    "extendedStandingPosition",
    "heavyLoads",
];

const defaultEnvironment = (): Environment => ({
    negativeCold: true,
    positiveCold: true,
    noise: true,
    smells: true,
    dust: true,
    sustainedPace: true,
    extendedStandingPosition: true,
    heavyLoads: true,
});

const workerExtended = computed(() => props.worker as WorkerDetailWithEnvironment);

const normalizeTeamPreferenceForSelect = (raw: string | undefined | null): string => {
    if (raw == null || raw === "") return "";
    return String(raw).trim().toLowerCase();
};

const teamPreferenceModelValue = computed(() =>
    normalizeTeamPreferenceForSelect(
        (preferences.value.teamPreference as string | undefined) ?? props.worker.teamPreference,
    ),
);

const teamOptions = computed(() => [
    { label: t("page.interim.workConditions.teamPreference.alone"), value: "alone" },
    { label: t("page.interim.workConditions.teamPreference.team"), value: "team" },
    { label: t("page.interim.workConditions.teamPreference.both"), value: "both" },
]);

const resolvedEnvironment = computed((): Environment => ({
    ...defaultEnvironment(),
    ...workerExtended.value.environment,
    ...(preferences.value.environment as Partial<Environment> | undefined),
}));

const handleAvailabilitiesUpdate = (payload: WorkerPreferences) => {
    preferences.value = {
        ...preferences.value,
        ...payload,
    };
    debouncedPatchAvailabilities?.(payload);
};

const handleTeamPreference = (value: string) => {
    preferences.value = { ...preferences.value, teamPreference: value };
    debouncedPersistWorkConditions?.();
};

const handleEnvironmentToggle = (key: keyof Environment) => {
    const next: Environment = {
        ...resolvedEnvironment.value,
        [key]: !resolvedEnvironment.value[key],
    };
    preferences.value = { ...preferences.value, environment: next };
    debouncedPersistWorkConditions?.();
};
</script>
<template>
    <div class="preferences-tab">
        <div class="preferences-tab__weekly-section">
            <WorkerAvailabilities
                :worker="worker"
                :edition="true"
                :readonly="false"
                :show-form-section-separator="false"
                :show-form-section-subtitle="false"
                compact-availability-column-labels
                @update="handleAvailabilitiesUpdate"
            />
        </div>

        <div class="preferences-tab__section-separator" aria-hidden="true">
            <Separator />
        </div>

        <FormSection size="md">
            <template #title>
                <h2 class="preferences-tab__work-conditions-title typo-sm-medium">
                    {{ t("page.interim.workConditions.title") }}
                </h2>
            </template>
        </FormSection>
        <div class="preferences-tab__work-conditions-card">
            <div class="preferences-tab__work-row">
                <span class="preferences-tab__work-row-label typo-xxs-medium">
                    {{ t("page.interim.profile.interview.preferences.teamWorkLabel") }}
                </span>
                <Select
                    :options="teamOptions"
                    width="180px"
                    size="xs"
                    :model-value="teamPreferenceModelValue"
                    @update:model-value="handleTeamPreference"
                />
            </div>
            <template v-for="environmentKey in ENVIRONMENT_KEYS" :key="environmentKey">
                <div class="preferences-tab__work-row-separator" aria-hidden="true" />
                <div class="preferences-tab__work-row">
                    <span class="preferences-tab__work-row-label typo-xxs-medium">
                        {{ t(`page.interim.workConditions.environmentConditions.${environmentKey}`) }}
                    </span>
                    <Toggle
                        :active="resolvedEnvironment[environmentKey]"
                        size="sm"
                        @input="handleEnvironmentToggle(environmentKey)"
                    />
                </div>
            </template>
        </div>
    </div>
</template>
<style scoped lang="scss">
.preferences-tab {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3xl, 24px);
    width: 100%;

    &__weekly-section {
        padding-bottom: var(--spacing-xl, 16px);
    }

    &__work-conditions-title {
        margin: 0;
        color: var(--neutral-700, #323848);
    }

    &__section-separator {
        flex-shrink: 0;
        width: calc(100% + 2 * var(--spacing-3xl, 24px));
        margin-right: calc(-1 * var(--spacing-3xl, 24px));
        margin-left: calc(-1 * var(--spacing-3xl, 24px));
    }

    &__work-conditions-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        padding: var(--spacing-md, 8px);
        border-radius: var(--radius-xl, 12px);
        border: 1px solid var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);
        box-shadow: var(--regular-shadow-xs, 0px 1px 2px #dbdfe640);
    }

    &__work-row-separator {
        flex-shrink: 0;
        height: 1px;
        margin: var(--spacing-xxs, 2px) 0;
        background-color: var(--stroke-soft-200, #dbdfe6);
    }

    &__work-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-md, 8px);
        min-height: 36px;
        padding: 0 var(--spacing-xs, 4px);
    }

    &__work-row-label {
        flex: 1;
        min-width: 0;
        color: var(--text-sub-600, #494e61);
    }
}
</style>
